import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { useHotkeys } from "../../helper-components/use-hotkeys";
import SelectField from "../../forms/select-field";
import AddressSidebar from "../../common-components/address-bar";
import errorToast from "../../helper-components/error-toast";
import { purchaseVendorsForItemBatchGetAction } from "../../../redux/actions/purchase-action";
import {
  getRowAllocationSummary,
  toNumber,
} from "../../../utils/purchase-return-calculations";

// SelectField/AddressSidebar read/write through Formik's `values` by field
// name, but this panel's batches live in the parent's `rows` state, not
// Formik state — so each batch gets its own scoped, throwaway field name(s)
// here just to drive those components; selections sync back via callbacks.
const uomFieldName = (batch) => `bulk_allocate_uom_${batch.key}`;
const vendorFieldName = (batch) => `bulk_allocate_vendor_${batch.key}`;
const addressFieldPrefix = (batchKey) => `bulk_allocate_addr_${batchKey}`;

const STATUS_LABEL = {
  not_allocated: "Not allocated",
  partial: "Partially allocated",
  full: "Fully allocated",
};
const STATUS_CLASS = {
  not_allocated: "bg-[var(--dashboard-gray-100)] text-[var(--dashboard-gray-600)]",
  partial: "bg-[var(--dashboard-bg-warning)] text-[var(--dashboard-warning-dark)]",
  full: "bg-[var(--status-badge-green-bg)] text-[var(--status-badge-green-fg)]",
};

const formatMoney = (value) =>
  `₹${toNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fieldLabelClass =
  "text-[10px] uppercase tracking-wide text-[var(--trans-stats-con-label-fg)] font-semibold";
const fieldSubLabelClass =
  "text-[10px] text-[var(--description-color)] mt-1 whitespace-nowrap";
const fieldInputClass =
  "w-full border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--input-color)] rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[var(--input-focus)]";
const linkButtonClass =
  "text-[10px] font-semibold text-[var(--primary)] hover:underline whitespace-nowrap disabled:opacity-50 disabled:no-underline";

const BatchCard = ({
  batch,
  uomOptions,
  onChange,
  onRemove,
  vendorPickerOpen,
  vendors,
  loadingVendors,
  onToggleVendorPicker,
  onSelectVendor,
  onOpenAddressPicker,
}) => {
  const isExpired = (() => {
    const parsed = new Date(batch.item_exp_date);
    return !Number.isNaN(parsed.getTime()) && parsed < new Date();
  })();
  const shipCity = batch.cmr_ship_to_address?.cmr_ship_address_city;
  const shipPincode = batch.cmr_ship_to_address?.cmr_ship_address_pincode;
  const payCity = batch.cmr_pay_to_address?.cmr_pay_address_city;
  const unitName = (batch.base_uom_name || "unit").toLowerCase();
  const vendorOptions = (vendors || []).map((v) => ({
    value: v.cmr_id,
    label: `${v.cmr_name} (${v.cmr_code})`,
    raw: v,
  }));

  return (
    <div className="bg-[var(--create-table-row-bg)] border border-[var(--create-table-border)] border-l-4 border-l-[var(--primary)] rounded-md p-3 flex flex-col gap-2">
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-[var(--status-badge-blue-bg)] text-[var(--status-badge-blue-fg)] text-xs font-semibold px-2 py-0.5 rounded">
            {batch.item_batch_number}
          </span>
          <span className="bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)] border border-[var(--status-badge-red-border)] text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
            Exp {formatDate(batch.item_exp_date)}
            {isExpired ? " · expired" : ""}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <div className="text-right">
            <div className="text-sm font-semibold text-[var(--heading-color)]">
              {batch.cmr_name}
            </div>
            <div className="text-xs text-[var(--description-color)] whitespace-nowrap">
              Ship {shipCity || "-"} {shipPincode || ""} · Pay {payCity || "-"}
            </div>
            <div className="flex items-center gap-3 justify-end mt-1">
              <button type="button" onClick={onOpenAddressPicker} className={linkButtonClass}>
                Change Address
              </button>
              <button
                type="button"
                onClick={onToggleVendorPicker}
                disabled={loadingVendors}
                className={linkButtonClass}
              >
                {loadingVendors ? "Loading vendors…" : "Change Vendor"}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            title="Deallocate this batch"
            className="w-5 h-5 shrink-0 flex items-center justify-center text-[var(--description-color)] hover:text-[var(--heading-color)]"
          >
            ✕
          </button>
        </div>
      </div>

      {vendorPickerOpen && (
        <SelectField
          name={vendorFieldName(batch)}
          options={vendorOptions}
          containerClass=""
          parentClass="w-full"
          isSearchable
          isClearable={false}
          errMsgContainerClass="hidden"
          selectClass="text-sm"
          placeholder={loadingVendors ? "Loading vendors…" : "Select a vendor"}
          isDisabled={loadingVendors}
          handleSelected={(selected) => onSelectVendor(selected?.raw)}
        />
      )}

      <div className="text-m text-[var(--description-color)]">
        FROM BIN ({batch.to_bin_location || "-"})
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div>
          <span className={fieldLabelClass}>Qty</span>
          <div className="flex items-center border border-[var(--input-border)] rounded-md overflow-hidden mt-1">
            <button
              type="button"
              disabled={toNumber(batch.qty) <= 0}
              onClick={() => onChange({ qty: toNumber(batch.qty) - 1 })}
              className="w-7 h-8 shrink-0 flex items-center justify-center text-[var(--nav-heading-color)] hover:bg-[var(--row-hover-color)] disabled:opacity-30"
            >
              −
            </button>
            <input
              type="number"
              min={0}
              max={batch.total_allocatable_qty}
              value={batch.qty}
              onChange={(e) => onChange({ qty: e.target.value })}
              className="w-full text-center text-sm font-semibold border-x border-[var(--input-border)] py-1.5 focus:outline-none"
            />
            <button
              type="button"
              disabled={toNumber(batch.qty) >= toNumber(batch.total_allocatable_qty)}
              onClick={() => onChange({ qty: toNumber(batch.qty) + 1 })}
              className="w-7 h-8 shrink-0 flex items-center justify-center text-[var(--nav-heading-color)] hover:bg-[var(--row-hover-color)] disabled:opacity-30"
            >
              +
            </button>
          </div>
          <div className={fieldSubLabelClass}>available {batch.total_allocatable_qty}</div>
        </div>

        <div>
          <span className={fieldLabelClass}>Uom</span>
          <SelectField
            name={uomFieldName(batch)}
            options={
              uomOptions.length ? uomOptions : [{ value: batch.item_uom_id, label: batch.item_uom }]
            }
            containerClass="mt-1"
            parentClass="w-full"
            isSearchable={false}
            isClearable={false}
            errMsgContainerClass="hidden"
            selectClass="text-sm"
            handleSelected={(selected) => onChange({ item_uom_id: selected?.value })}
          />
          <div className={fieldSubLabelClass}>UOM</div>
        </div>

        <div>
          <span className={fieldLabelClass}>Rate</span>
          <input
            type="number"
            min={0}
            value={batch.purchase_rate}
            onChange={(e) => onChange({ purchase_rate: e.target.value })}
            className={`${fieldInputClass} mt-1`}
          />
          <div className={fieldSubLabelClass}>per {unitName}</div>
        </div>

        <div>
          <span className={fieldLabelClass}>Discount</span>
          <input
            type="number"
            min={0}
            value={batch.discount_amount}
            onChange={(e) => onChange({ discount_amount: e.target.value })}
            className={`${fieldInputClass} mt-1`}
          />
          <div className={fieldSubLabelClass}>flat ₹</div>
        </div>

        <div>
          <span className={fieldLabelClass}>Tax</span>
          <div
            className={`${fieldInputClass} mt-1 bg-[var(--toggle-disabled-background)] text-[var(--description-color)]`}
          >
            {formatMoney(batch.tax_amount)}
          </div>
          <div className={fieldSubLabelClass}>{batch.tax_percentage}% of net</div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-[var(--create-table-border)] text-xs text-[var(--description-color)]">
        <div>
          {batch.qty} × {formatMoney(batch.purchase_rate)} = {formatMoney(batch.base_amount)}
          {" | "}disc {formatMoney(batch.discount_amount)}
          {" | "}tax {formatMoney(batch.tax_amount)} ({batch.tax_percentage}%)
        </div>
        <div className="text-sm font-bold text-[var(--primary)] whitespace-nowrap ml-2">
          Total {formatMoney(batch.total_amount)}
        </div>
      </div>
    </div>
  );
};

const BulkAllocateSidePanel = ({ open, setOpen, row, onUpdateBatch, onChangeVendor }) => {
  const close = useRef(null);
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext();
  const batches = row?.batches || [];

  const [vendorPickerBatchKey, setVendorPickerBatchKey] = useState(null);
  const [vendorsByBatch, setVendorsByBatch] = useState({});
  const [loadingVendorsKey, setLoadingVendorsKey] = useState(null);
  const [addressPickerBatchKey, setAddressPickerBatchKey] = useState(null);
  // Separate from addressPickerBatchKey (which only controls whether
  // AddressSidebar is visually open): AddressSidebar's own Cancel/Save both
  // call setOpen(false) in the same batch of updates that sets its Formik
  // address fields, so gating our read of those fields on addressPickerBatchKey
  // means it's already null by the time a render could see the new values.
  // This key instead only clears when the picker fully closes.
  const [addressSyncTargetKey, setAddressSyncTargetKey] = useState(null);

  useEffect(() => {
    if (open && row) {
      (row.batches || []).forEach((batch) => {
        setFieldValue(uomFieldName(batch), batch.item_uom_id, false);
      });
    } else if (!open) {
      setVendorPickerBatchKey(null);
      setVendorsByBatch({});
      setAddressPickerBatchKey(null);
      setAddressSyncTargetKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.item_id]);

  useHotkeys("esc", () => {
    // While the address picker is open, let it handle its own Escape close
    // (Headless UI's Dialog does this natively) instead of also closing us.
    if (addressPickerBatchKey) return;
    if (close.current) {
      close.current.click();
    }
  });

  const handleToggleVendorPicker = (batch) => {
    if (vendorPickerBatchKey === batch.key) {
      setVendorPickerBatchKey(null);
      return;
    }
    setVendorPickerBatchKey(batch.key);
    if (!vendorsByBatch[batch.key]) {
      setLoadingVendorsKey(batch.key);
      dispatch(
        purchaseVendorsForItemBatchGetAction(
          { item_id: row.item_id, item_batch_number: batch.item_batch_number, qty: batch.qty },
          (resp) => {
            setLoadingVendorsKey(null);
            setVendorsByBatch((prev) => ({ ...prev, [batch.key]: resp?.data?.vendors || [] }));
          },
          (err) => {
            setLoadingVendorsKey(null);
            errorToast(err?.data?.message || err?.message || "Failed to load vendors");
          }
        )
      );
    }
  };

  const handleSelectVendor = (batchIndex, vendor) => {
    if (!vendor) return;
    onChangeVendor(batchIndex, vendor);
    setVendorPickerBatchKey(null);
  };

  // Opening the picker seeds AddressSidebar's own fields with the batch's
  // current address first, so its onSuccess (which prefers
  // values[name_ship_to_address]/_pay_to_address over the default when
  // present) highlights/expands the address already assigned to this batch
  // instead of always falling back to the vendor's default address.
  const handleOpenAddressPicker = (batch) => {
    const prefix = addressFieldPrefix(batch.key);
    setFieldValue(`${prefix}_ship_to_address`, batch.cmr_ship_to_address, false);
    setFieldValue(`${prefix}_pay_to_address`, batch.cmr_pay_to_address, false);
    setAddressPickerBatchKey(batch.key);
    setAddressSyncTargetKey(batch.key);
  };

  // Only clears the *visual* open state. addressSyncTargetKey deliberately
  // stays set: this is called by AddressSidebar's own setOpen(false), which
  // Save invokes in the very same synchronous handler as the setFieldValue
  // calls that write the new address — clearing addressSyncTargetKey here
  // too would race the sync effect below out of ever seeing that update, the
  // same bug as before just moved one level down. It only gets reassigned
  // when a different batch's picker opens, or reset when the panel closes.
  const handleCloseAddressPicker = () => {
    setAddressPickerBatchKey(null);
  };

  // Watches those same fields and pushes whatever's in them onto the batch.
  // Fires once right after the seed above (a harmless no-op re-apply of the
  // batch's own current address) and again whenever AddressSidebar's Save
  // writes a new selection — React only re-runs this when the watched
  // value's reference actually changes, so leaving addressSyncTargetKey set
  // after closing doesn't cause repeat/stale firing.
  const syncPrefix = addressSyncTargetKey ? addressFieldPrefix(addressSyncTargetKey) : null;
  const syncShipValue = syncPrefix ? values[`${syncPrefix}_ship_to_address`] : null;
  const syncPayValue = syncPrefix ? values[`${syncPrefix}_pay_to_address`] : null;

  useEffect(() => {
    if (addressSyncTargetKey && syncShipValue && syncPayValue) {
      const batchIndex = batches.findIndex((b) => b.key === addressSyncTargetKey);
      if (batchIndex >= 0) {
        onUpdateBatch(batchIndex, {
          cmr_ship_to_address: syncShipValue,
          cmr_pay_to_address: syncPayValue,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncShipValue, syncPayValue]);

  const activeAddressBatch = batches.find((b) => b.key === addressPickerBatchKey) || null;

  const summary = getRowAllocationSummary({ batches });
  const progressPct =
    summary.required > 0 ? Math.min((summary.qty / summary.required) * 100, 100) : 0;

  return (
    <>
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          // AddressSidebar renders as a separate, sibling Headless UI Dialog
          // (not a React descendant of this one), so Headless UI treats
          // clicks inside it as "outside" this panel and would otherwise
          // close both at once. Ignore that while the picker is open — its
          // own explicit Cancel/Save and the header/footer close buttons
          // (which call setOpen directly, bypassing this handler) still work.
          if (addressPickerBatchKey) return;
          setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[640px] bg-[var(--create-table-row-bg)] rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                {/* Header */}
                <div className="p-4 flex justify-between items-start bg-[var(--create-table-header-bg)]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[var(--create-table-header-fg)] font-semibold">
                      Allocate Batches
                    </div>
                    <div className="text-base sm:text-lg font-bold text-[var(--create-table-header-fg)] leading-snug">
                      {row?.item_name}
                    </div>
                  </div>
                  <button
                    ref={close}
                    onClick={() => setOpen(false)}
                    className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-white/10 text-[var(--create-table-header-fg)] hover:bg-white/20"
                  >
                    ✕
                  </button>
                </div>

                {/* Stats bar */}
                <div className="px-4 py-3 bg-[var(--slide-panel-head-bg)] border-b border-[var(--slide-panel-border)] flex flex-col gap-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-6">
                      <div>
                        <div className={fieldLabelClass}>Required</div>
                        <div className="text-2xl font-bold text-[var(--trans-stats-con-value-fg)]">
                          {summary.required}
                        </div>
                      </div>
                      <div>
                        <div className={fieldLabelClass}>Allocated</div>
                        <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                          {summary.qty}
                        </div>
                      </div>
                      <div>
                        <div className={fieldLabelClass}>Remaining</div>
                        <div className="text-2xl font-bold text-[var(--trans-stats-con-orange-fg)]">
                          {summary.remaining}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${STATUS_CLASS[summary.status]}`}
                      >
                        {STATUS_LABEL[summary.status]}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-[var(--gray-200)] overflow-hidden">
                    <div
                      className="h-full bg-[var(--trans-stats-con-orange-fg)] transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="space-y-3 p-4 flex-1 overflow-y-auto">
                  {batches.length === 0 ? (
                    <div className="text-sm text-[var(--description-color)] text-center py-8">
                      No batches available for this item.
                    </div>
                  ) : (
                    batches.map((batch, index) => (
                      <BatchCard
                        key={batch.key}
                        batch={batch}
                        uomOptions={row?.uom_dropdown || []}
                        onChange={(changes) => onUpdateBatch(index, changes)}
                        onRemove={() => onUpdateBatch(index, { qty: 0 })}
                        vendorPickerOpen={vendorPickerBatchKey === batch.key}
                        vendors={vendorsByBatch[batch.key]}
                        loadingVendors={loadingVendorsKey === batch.key}
                        onToggleVendorPicker={() => handleToggleVendorPicker(batch)}
                        onSelectVendor={(vendor) => handleSelectVendor(index, vendor)}
                        onOpenAddressPicker={() => handleOpenAddressPicker(batch)}
                      />
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-[var(--slide-panel-border)] bg-[var(--create-table-row-bg)] sticky bottom-0 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-6 py-2 rounded-md fw_600 bg-[var(--primary)] text-white hover:opacity-90"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {activeAddressBatch && (
      <AddressSidebar
        type="vendor"
        page="add"
        disabled={false}
        path=""
        open={!!addressPickerBatchKey}
        setOpen={(isOpen) => {
          if (!isOpen) handleCloseAddressPicker();
        }}
        id={activeAddressBatch.cmr_id}
        name={addressFieldPrefix(addressPickerBatchKey)}
        handleDraft={() => errorToast("Add a new address from the vendor's profile")}
      />
    )}
    </>
  );
};

export default BulkAllocateSidePanel;
